import React from "react";

/**
 * Real Estate Services illustration — stylized city skyline with growth chart,
 * location pin, and "SOLD" tag. Clean dark, cinematic palette.
 */
const RealEstateIllustration = () => (
  <svg
    viewBox="0 0 600 360"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice"
    style={{ width: "100%", height: "100%", display: "block" }}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="re-bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0a1e3d" />
        <stop offset="50%" stopColor="#0b2c5c" />
        <stop offset="100%" stopColor="#062043" />
      </linearGradient>
      <linearGradient id="re-skyline" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#4d7cd6" />
        <stop offset="100%" stopColor="#1e3a8a" />
      </linearGradient>
      <linearGradient id="re-skyline-tall" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#6e8cff" />
        <stop offset="100%" stopColor="#2c4ec9" />
      </linearGradient>
      <linearGradient id="re-cyan" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#67e8f9" />
        <stop offset="100%" stopColor="#06b6d4" />
      </linearGradient>
      <linearGradient id="re-glow" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="rgba(34,211,238,0.4)" />
        <stop offset="100%" stopColor="rgba(34,211,238,0)" />
      </linearGradient>
      <filter id="re-blur" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="6" />
      </filter>
    </defs>

    <rect width="600" height="360" fill="url(#re-bg)" />

    {/* Ambient blobs */}
    <circle cx="100" cy="80" r="100" fill="#22d3ee" opacity="0.15" filter="url(#re-blur)" />
    <circle cx="500" cy="280" r="120" fill="#6e8cff" opacity="0.2" filter="url(#re-blur)" />

    {/* Subtle horizontal grid (skyline floor) */}
    <g opacity="0.15" stroke="#67e8f9" strokeWidth="0.5">
      {Array.from({ length: 8 }).map((_, i) => (
        <line key={i} x1="0" y1={280 + i * 10} x2="600" y2={280 + i * 10} />
      ))}
    </g>

    {/* Skyline reflection (subtle ground glow) */}
    <rect x="0" y="280" width="600" height="80" fill="url(#re-glow)" opacity="0.5" />

    {/* SKYLINE — back row (taller, lighter) */}
    <g>
      {/* Tower 1 */}
      <rect x="50" y="160" width="40" height="140" fill="url(#re-skyline)" />
      {/* Tower 2 - tallest */}
      <rect x="100" y="100" width="55" height="200" fill="url(#re-skyline-tall)" />
      <rect x="118" y="90" width="20" height="10" fill="url(#re-skyline-tall)" />
      <line x1="128" y1="80" x2="128" y2="90" stroke="#67e8f9" strokeWidth="2" />
      <circle cx="128" cy="78" r="2" fill="#fbbf24" />
      {/* Tower 3 */}
      <rect x="165" y="180" width="35" height="120" fill="url(#re-skyline)" />
      {/* Tower 4 */}
      <rect x="210" y="130" width="50" height="170" fill="url(#re-skyline-tall)" />
      <polygon points="210,130 235,108 260,130" fill="url(#re-skyline-tall)" />
      {/* Tower 5 */}
      <rect x="270" y="170" width="38" height="130" fill="url(#re-skyline)" />
      {/* Tower 6 */}
      <rect x="318" y="145" width="48" height="155" fill="url(#re-skyline-tall)" />
      {/* Tower 7 */}
      <rect x="376" y="195" width="36" height="105" fill="url(#re-skyline)" />
      {/* Tower 8 */}
      <rect x="422" y="120" width="50" height="180" fill="url(#re-skyline-tall)" />
      <rect x="438" y="105" width="18" height="15" fill="url(#re-skyline-tall)" />
      {/* Tower 9 */}
      <rect x="482" y="175" width="40" height="125" fill="url(#re-skyline)" />
      {/* Tower 10 */}
      <rect x="532" y="155" width="42" height="145" fill="url(#re-skyline-tall)" />

      {/* Windows — vertical light pattern */}
      <g fill="#22d3ee" opacity="0.85">
        {/* Tower 2 windows */}
        {Array.from({ length: 14 }).map((_, row) =>
          Array.from({ length: 3 }).map((_, col) => (
            <rect
              key={`t2-${row}-${col}`}
              x={108 + col * 14}
              y={120 + row * 13}
              width="6"
              height="6"
              opacity={Math.random() > 0.4 ? 1 : 0.3}
            />
          ))
        )}
        {/* Tower 4 windows */}
        {Array.from({ length: 11 }).map((_, row) =>
          Array.from({ length: 3 }).map((_, col) => (
            <rect
              key={`t4-${row}-${col}`}
              x={218 + col * 13}
              y={142 + row * 13}
              width="5"
              height="5"
              opacity={Math.random() > 0.5 ? 1 : 0.25}
            />
          ))
        )}
        {/* Tower 6 windows */}
        {Array.from({ length: 10 }).map((_, row) =>
          Array.from({ length: 3 }).map((_, col) => (
            <rect
              key={`t6-${row}-${col}`}
              x={325 + col * 13}
              y={158 + row * 13}
              width="5"
              height="5"
              opacity={Math.random() > 0.5 ? 1 : 0.3}
            />
          ))
        )}
        {/* Tower 8 windows */}
        {Array.from({ length: 11 }).map((_, row) =>
          Array.from({ length: 3 }).map((_, col) => (
            <rect
              key={`t8-${row}-${col}`}
              x={430 + col * 13}
              y={134 + row * 13}
              width="5"
              height="5"
              opacity={Math.random() > 0.4 ? 1 : 0.3}
            />
          ))
        )}
      </g>
    </g>

    {/* Floating "TRENDING UP" growth chart card (top-right) */}
    <g transform="translate(390, 35)">
      <rect width="180" height="100" rx="12" fill="rgba(10, 30, 60, 0.95)" stroke="rgba(103,232,249,0.5)" strokeWidth="1.2" />
      <text x="14" y="22" fill="#fff" fontSize="10" fontWeight="700" fontFamily="Poppins, sans-serif">PORTFOLIO VALUE</text>
      <text x="14" y="44" fill="#67e8f9" fontSize="20" fontWeight="800" fontFamily="Poppins, sans-serif">+24.6%</text>
      <text x="14" y="58" fill="#67e8f9" fontSize="8" fontFamily="Poppins, sans-serif" opacity="0.7">YEAR OVER YEAR</text>

      {/* Trend line */}
      <polyline
        points="14,90 36,82 58,76 80,68 102,72 124,58 146,48 168,36"
        fill="none"
        stroke="#22d3ee"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Dots on the trend line */}
      <circle cx="168" cy="36" r="3.5" fill="#22d3ee" />
      <circle cx="168" cy="36" r="7" fill="#22d3ee" opacity="0.3" />
      {/* Trend arrow */}
      <path d="M 170 30 L 178 22 L 174 22 L 174 18 L 178 18 L 178 22 Z" fill="#67e8f9" opacity="0.8" />
    </g>

    {/* Location pin (bottom-left) */}
    <g transform="translate(40, 200)">
      {/* Pin shadow */}
      <ellipse cx="22" cy="90" rx="14" ry="3" fill="rgba(0,0,0,0.4)" />
      {/* Pin body */}
      <path
        d="M 22 10 C 33 10 40 18 40 28 C 40 42 22 80 22 80 C 22 80 4 42 4 28 C 4 18 11 10 22 10 Z"
        fill="url(#re-cyan)"
        stroke="#fff"
        strokeWidth="1"
      />
      {/* Pin inner circle */}
      <circle cx="22" cy="28" r="7" fill="#0a1e3d" />
      <circle cx="22" cy="28" r="3" fill="#67e8f9" />
      {/* Pulse ring */}
      <circle cx="22" cy="85" r="4" fill="none" stroke="#67e8f9" strokeWidth="1.5" opacity="0.6">
        <animate attributeName="r" values="4;14;4" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.7;0;0.7" dur="2s" repeatCount="indefinite" />
      </circle>
    </g>

    {/* "SOLD" tag (top-left) */}
    <g transform="translate(40, 50) rotate(-8 60 18)">
      <rect width="98" height="36" rx="6" fill="#22d3ee" />
      <circle cx="10" cy="18" r="3.5" fill="#0a1e3d" />
      <text x="48" y="23" fill="#0a1e3d" fontSize="13" fontWeight="800" fontFamily="Poppins, sans-serif" textAnchor="middle" letterSpacing="2">SOLD</text>
      <text x="48" y="32" fill="#0a1e3d" fontSize="6" fontFamily="Poppins, sans-serif" textAnchor="middle" letterSpacing="1" opacity="0.7">PREMIUM PROPERTY</text>
    </g>

    {/* Animated stars/sparkles */}
    <circle cx="280" cy="50" r="1.5" fill="#fff" opacity="0.6">
      <animate attributeName="opacity" values="0.3;1;0.3" dur="2.4s" repeatCount="indefinite" />
    </circle>
    <circle cx="350" cy="80" r="1" fill="#fff" opacity="0.5">
      <animate attributeName="opacity" values="0.2;0.8;0.2" dur="3s" repeatCount="indefinite" />
    </circle>
    <circle cx="200" cy="40" r="1.2" fill="#fff" opacity="0.5">
      <animate attributeName="opacity" values="0.3;0.9;0.3" dur="2.7s" repeatCount="indefinite" />
    </circle>
  </svg>
);

export default RealEstateIllustration;
